import { useState } from "react";
import { useGetNotificationsQuery, useMarkAsReadNotificationMutation } from "services";
import { Bell, ChevronLeft, ChevronRight, Loading, Button, Dropdown } from "components";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

enum NotificationType {
  NEW_PARKING = "NEW_PARKING",
  NEW_COMPANY = "NEW_COMPANY",
}

interface Notification {
  notification_type: string | number;
  notification_id: string | number;
  notification_tags: string[];
  author: { name: string };
  notification: string;
  is_read: boolean;
}

export const NotificationsDropdown = () => {
  const { data: notifications = [], refetch } = useGetNotificationsQuery({});
  const { refetch: refetchUnread } = useGetNotificationsQuery({ is_read: false });
  const [readNotification] = useMarkAsReadNotificationMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  const markAsRead = async (id: number) => {
    setLoading(true);
    await readNotification(id);
    await refetch();
    setLoading(false);
    await refetchUnread();
  };

  const handleParkingAction = (notificationType: NotificationType, extractedId: number | null) => {
    if (extractedId === null) {
      console.error("No valid ID extracted from notification message");
      return;
    }
    navigate("/parking-management", {
      state: {
        openModal: true,
        notificationType,
        notificationId: extractedId,
        fromNotification: true,
      },
    });
  };

  const renderNotificationContent = (notif: Notification) => {
    const { notification_type, notification_id, notification_tags, author, notification: message, is_read } = notif;
    const normalizedType = String(notification_type) as NotificationType;
    const notificationIdNum = typeof notification_id === "string" ? parseInt(notification_id, 10) : notification_id;

    const getNotificationMessage = () => {
      const linkText = "CLICK HERE";
      // Extract ID from the message
      const idMatch = message.match(/^\[(\d+)]/);
      const extractedId = idMatch ? Number(idMatch[1]) : null;
      console.log("Extracted ID:", extractedId);
      const messageWithoutId = message.replace(/^\[\d+]\s*/, "");
      const messageParts = messageWithoutId.split(linkText);

      return (
        <>
          {messageParts[0]}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleParkingAction(normalizedType, extractedId);
            }}
            className="cursor-pointer text-base font-bold underline"
          >
            {linkText}
          </a>
          {messageParts[1]}
        </>
      );
    };

    switch (normalizedType) {
      case NotificationType.NEW_PARKING:
        const newParkingIdMatch = message.match(/^\[(\d+)]/);
        const newParkingExtractedId = newParkingIdMatch ? Number(newParkingIdMatch[1]) : null;
        return (
          <div className="space-y-2 rounded border border-blue-500 p-4">
            <p className="text-base text-sm">
              {t("notifications.notificationTags")} {notification_tags.join(" | ")}
            </p>
            <p className="text-base font-bold">
              {t("notifications.author")} {author.name}
            </p>
            <p className="text-base">{getNotificationMessage()}</p>
            <p className="py-3 text-base text-xs">{t("notifications.theConfigurationIsMandatory")}</p>
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <Button
                  className="bg-green-500 px-6 uppercase text-white"
                  onClick={() => handleParkingAction(NotificationType.NEW_PARKING, newParkingExtractedId)}
                  disabled={loading || newParkingExtractedId === null}
                >
                  {t("notifications.configureNow")}
                </Button>
              </div>
              <NavigationButtons />
            </div>
          </div>
        );
      case NotificationType.NEW_COMPANY:
        const newCompanyIdMatch = message.match(/^\[(\d+)]/);
        const newCompanyExtractedId = newCompanyIdMatch ? Number(newCompanyIdMatch[1]) : null;
        return (
          <div className="space-y-2 rounded border border-blue-500 p-4">
            <p className="text-base text-sm">
              {t("notifications.notificationTags")} {notification_tags.join(" | ")}
            </p>
            <p className="text-base font-bold">
              {t("notifications.author")} {author.name}
            </p>
            <p className="text-base">{getNotificationMessage()}</p>
            <p className="py-3 text-base text-xs">{t("notifications.theConfigurationIsMandatory")}</p>
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <Button
                  className="bg-green-500 px-6 uppercase text-white"
                  onClick={() => handleParkingAction(NotificationType.NEW_COMPANY, newCompanyExtractedId)}
                  disabled={loading || newCompanyExtractedId === null}
                >
                  {t("notifications.configureNow")}
                </Button>
              </div>
              <NavigationButtons />
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-2 rounded border border-blue-500 p-4">
            <p className="text-sm text-gray-600">
              {t("notifications.notificationTags")} {notification_tags.join(" | ")}
            </p>
            <p className="text-base font-bold">
              {t("notifications.author")} {author.name}
            </p>
            <p className="text-base">{message}</p>
            <div className="flex items-center justify-between">
              <Button
                className={`px-6 uppercase ${is_read ? "bg-secondary" : "bg-success"}`}
                onClick={() => markAsRead(notificationIdNum)}
                disabled={loading}
              >
                {is_read ? t("notifications.read") : t("notifications.markAsRead")}
              </Button>
              <NavigationButtons />
            </div>
          </div>
        );
    }
  };

  const NavigationButtons = () => (
    <div className="flex items-center space-x-0.5">
      <Button
        className="w-fit border border-primary bg-white p-2 px-3 disabled:cursor-auto disabled:opacity-20"
        onClick={() => setIndex(index - 1)}
        disabled={index === 0}
      >
        <ChevronLeft className="text-primary" />
      </Button>
      <Button
        className="w-fit border border-primary bg-white p-2 px-3 disabled:cursor-auto disabled:opacity-20"
        onClick={() => setIndex(index + 1)}
        disabled={!notifications || index === notifications.length - 1}
      >
        <ChevronRight className="text-primary" />
      </Button>
    </div>
  );

  return (
    <Dropdown>
      <Dropdown.Trigger>
        <div className="cursor-pointer">
          <Bell />
        </div>
      </Dropdown.Trigger>
      <Dropdown.Items className="w-96">
        {loading && <Loading absolute />}
        {!!notifications && notifications.length > 0 ? (
          renderNotificationContent({
            ...notifications[index],
            notification_tags: notifications[index].notification_tags.map(String),
            author: {
              ...notifications[index].author,
              name: String(notifications[index].author.name),
            },
          })
        ) : (
          <div className="p-4 text-center">
            <p className="text-gray-500">{t("notifications.noNewNotifications")}</p>
          </div>
        )}
      </Dropdown.Items>
    </Dropdown>
  );
};
